import { Link } from 'react-router-dom';
import { useTenants } from '../tenants';
import { FilaDeSistema } from './FilaDeSistema';
import { FormularioDeAltaDeSistema } from './FormularioDeAltaDeSistema';
import { useSistemas } from './consultas';
import { mensajeDeSistemas } from './mensajes';

/**
 * ABM de los sistemas conectados (KAN-144). Ruta `/admin/sistemas`, dentro de
 * `RequiereRol="ADMIN"`.
 *
 * Un sistema conectado es una **cuenta de servicio**: una aplicación que emite
 * comprobantes con una API key `fac_…`. No es una persona. Su authority es
 * `ROLE_SISTEMA` y **no existe forma de que sea `ROLE_ADMIN`** — solo emite,
 * nunca administra.
 *
 * # LAS TRES COSAS QUE ESTA PANTALLA TIENE QUE DEJAR CLARAS
 *
 * 1. **La API key se ve una sola vez.** La devuelven el alta y la rotación, y
 *    ningún otro endpoint la devuelve jamás. Ver {@link KeyEnClaro}.
 * 2. **Rotar es la respuesta a una key filtrada o perdida**, y deja fuera de
 *    servicio a la aplicación que la use hasta que la actualicen. Ver la
 *    confirmación en {@link FilaDeSistema}.
 * 3. **Emitir es lista blanca.** Sin fila en `sistemas_tenants`, `403`. Es lo
 *    que acota el daño de una key filtrada, y por eso se elige el tenant por
 *    nombre y no escribiendo un id. Ver {@link ListaBlanca}.
 *
 * # LOS TENANTS SE PIDEN UNA VEZ, ACÁ
 *
 * La lista de tenants la necesitan el formulario de alta y cada fila que abre su
 * lista blanca. Se consulta en la pantalla y baja por props: pedirla en cada fila
 * sería el mismo pedido repetido, y peor, dos filas podrían quedar mostrando
 * carteras distintas.
 */
export function PantallaDeSistemas() {
  const lista = useSistemas();
  const tenants = useTenants();

  const disponibles = tenants.data ?? [];

  return (
    <main>
      <p>
        <Link to="/admin">Administración</Link>
      </p>
      <h1>Sistemas conectados</h1>
      <p>
        Las aplicaciones que emiten comprobantes con una API key. No son personas: no entran acá,
        no administran nada y solo pueden facturar a nombre de los tenants que tengan autorizados.
      </p>

      {lista.isPending ? <p role="status">Cargando sistemas…</p> : null}

      {lista.isError ? (
        <p role="alert" className="error">
          {mensajeDeSistemas(lista.error, 'listar')}
        </p>
      ) : null}

      {tenants.isError ? (
        // La lista blanca se elige por nombre, así que sin tenants no se puede
        // elegir nada. Se dice explícitamente en vez de mostrar un selector
        // vacío que parecería decir "no hay clientes cargados".
        <p role="alert" className="error">
          No se pudo cargar la lista de tenants. Hasta que cargue no se puede autorizar a ningún
          sistema, porque el tenant se elige por nombre y no escribiendo un número.
        </p>
      ) : null}

      {lista.data ? (
        <table>
          <caption className="nota">
            Se listan los activos y los dados de baja. La baja es lógica: la key deja de autenticar
            en el acto, pero la fila queda para poder saber qué sistema pidió cada comprobante.
          </caption>
          <thead>
            <tr>
              <th scope="col">Nombre</th>
              <th scope="col">Prefijo de la key</th>
              <th scope="col">Key rotada</th>
              <th scope="col">Tenants</th>
              <th scope="col">Estado</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {lista.data.map((sistema) => (
              <FilaDeSistema key={sistema.id} sistema={sistema} tenants={disponibles} />
            ))}
          </tbody>
        </table>
      ) : null}

      <FormularioDeAltaDeSistema tenants={disponibles} />
    </main>
  );
}
